import { UNREAD_NOTIFICATION_COUNT_QUERY_OPTIONS } from '@/utils/fetchers/notification'
import {
	markTabNeedRefresh,
	resetPageNeedRefreshFlags,
} from '@/utils/hooks/usePageNeedRefreshSubscriber'
import useUser from '@/utils/hooks/useUser'
import { Stack } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { isEqual, partial } from 'lodash-es'
import { type HTMLAttributes, memo, MouseEvent } from 'react'
import AnimatedDotBubbleIcon from '../Icons/AnimatedDotBubbleIcon'
import AnimatedHomeIcon from '../Icons/AnimatedHomeIcon'
import AnimatedMagnifierIcon from '../Icons/AnimatedMagnifierIcon'
import AnimatedPersonalIcon from '../Icons/AnimatedPersonalIcon'
import AnimatedThreeDotsIcon from '../Icons/AnimatedThreeDotsIcon'
import UnreadNotificationTooltip from '../UnreadNotificationTooltip'
import SidebarTab from './SidebarTab'
import { Tab } from './config'
import { useActiveTab } from './util'

export interface TabBarProps extends HTMLAttributes<HTMLDivElement> {}

export default memo(function TabBar({ ...props }: TabBarProps) {
	const activeTab = useActiveTab()
	const user = useUser(void 0)

	const { data: unreadCount = 0 } = useQuery({
		...UNREAD_NOTIFICATION_COUNT_QUERY_OPTIONS,
		enabled: Boolean(user),
	})
	const visibleUnreadCount = user ? unreadCount : 0

	const handleTabClick = (tab: Tab, e: MouseEvent) => {
		if (activeTab === tab) {
			markTabNeedRefresh(tab)
			e.preventDefault()
		} else {
			resetPageNeedRefreshFlags()
		}
	}

	const renderUserProfileTab = () => {
		const content = (
			<SidebarTab
				isActive={activeTab === Tab.USER_PROFILE}
				renderIcon={() => (
					<AnimatedPersonalIcon isActive={activeTab === Tab.USER_PROFILE} style={{ zIndex: 1 }} />
				)}
				title="个人主页"
			/>
		)

		if (!user) {
			return (
				<Link to="/login" onClick={partial(handleTabClick, Tab.USER_PROFILE)} preload={false}>
					{content}
				</Link>
			)
		}

		return (
			<Link
				to="/u/$username"
				params={{ username: user.username }}
				onClick={partial(handleTabClick, Tab.USER_PROFILE)}
				preload="viewport"
			>
				{content}
			</Link>
		)
	}

	return (
		<Stack gap={12} {...props}>
			<Link
				to="/following"
				onClick={partial(handleTabClick, Tab.FOLLOWING_POST)}
				preload={user ? 'viewport' : false}
			>
				<SidebarTab
					isActive={activeTab === Tab.FOLLOWING_POST}
					renderIcon={() => (
						<AnimatedHomeIcon isActive={activeTab === Tab.FOLLOWING_POST} style={{ zIndex: 1 }} />
					)}
					title="动态"
				/>
			</Link>
			<Link
				to="/explore"
				onClick={partial(handleTabClick, Tab.EXPLORE_POST)}
				preload={user ? 'viewport' : false}
			>
				<SidebarTab
					isActive={activeTab === Tab.EXPLORE_POST}
					renderIcon={() => (
						<AnimatedMagnifierIcon
							isActive={activeTab === Tab.EXPLORE_POST}
							style={{ zIndex: 1 }}
						/>
					)}
					title="发现"
				/>
			</Link>
			<Link
				to="/notification"
				onClick={partial(handleTabClick, Tab.NOTIFICATION)}
				preload={user ? 'viewport' : false}
			>
				<SidebarTab
					isActive={activeTab === Tab.NOTIFICATION}
					renderIcon={() => (
						<UnreadNotificationTooltip data={visibleUnreadCount}>
							<AnimatedDotBubbleIcon
								isActive={activeTab === Tab.NOTIFICATION}
								showDot={visibleUnreadCount > 0}
								style={{ zIndex: 1 }}
							/>
						</UnreadNotificationTooltip>
					)}
					title="通知"
				/>
			</Link>
			{renderUserProfileTab()}
			<Link
				to="/more"
				onClick={partial(handleTabClick, Tab.MORE)}
				preload={user ? 'viewport' : false}
			>
				<SidebarTab
					isActive={activeTab === Tab.MORE}
					renderIcon={() => (
						<AnimatedThreeDotsIcon isActive={activeTab === Tab.MORE} style={{ zIndex: 1 }} />
					)}
					title="更多"
				/>
			</Link>
		</Stack>
	)
}, isEqual)
